// 🧭 NAVIGATION STORE - DÍA 5: SISTEMA DE ROUTING Y NAVEGACIÓN COMPLETADO
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { RouteLocationNormalized } from 'vue-router'

// 🎯 Interfaces
export interface BreadcrumbItem {
  text: string
  href?: string
  disabled?: boolean
  exact?: boolean
  icon?: string
}

export interface NavigationItem {
  id: string
  text: string
  icon: string
  route: string
  badge?: string | number
  badgeColor?: string
  roles?: string[]
}

export interface PageView {
  path: string
  name: string
  title: string
  timestamp: string
}

export interface HistoryItem {
  path: string
  name: string
  timestamp: number
  title: string
}

export interface FavoriteItem {
  path: string
  name: string
  title: string
  icon?: string
}

export interface AnalyticsEvent {
  category: string
  action: string
  label?: string
  value?: number
  custom_parameters?: Record<string, any>
  timestamp: string
}

export const useNavigationStore = defineStore('navigation', () => {
  // 🔧 State
  const currentRoute = ref<string>('/')
  const breadcrumbs = ref<BreadcrumbItem[]>([])
  const sidebarExpanded = ref<boolean>(true)
  const sidebarOpen = ref<boolean>(true)
  const pageTitle = ref<string>('MGI Dashboard')
  const navigationHistory = ref<HistoryItem[]>([])
  const pageViews = ref<PageView[]>([])
  const favorites = ref<FavoriteItem[]>([])
  const isLoading = ref<boolean>(false)
  const analyticsEvents = ref<AnalyticsEvent[]>([])
  const intendedRoute = ref<string | null>(null)

  // 📊 Computed Properties
  const canGoBack = computed(() => navigationHistory.value.length > 1)
  
  const recentPages = computed(() => 
    navigationHistory.value.slice(0, 5)
  )
  
  const favoriteRoutes = computed(() => 
    favorites.value.map(f => f.path)
  )

  // 🎯 Actions
  const setCurrentRoute = (route: string) => {
    currentRoute.value = route
  }

  const setLoading = (loading: boolean) => {
    isLoading.value = loading
  }

  const setBreadcrumbs = (items: BreadcrumbItem[]) => {
    breadcrumbs.value = items
  }

  const updateBreadcrumbs = (route: any) => {
    const items: BreadcrumbItem[] = []
    
    // Always add home
    items.push({
      text: 'Inicio',
      href: '/',
      disabled: false,
      exact: true,
      icon: 'mdi-home'
    })

    if (route.meta?.breadcrumb && route.path !== '/') {
      items.push({
        text: route.meta.breadcrumb,
        href: route.path,
        disabled: true,
        exact: true,
        icon: route.meta.icon
      })
    }

    setBreadcrumbs(items)
  }

  const generateBreadcrumbs = (route: RouteLocationNormalized): BreadcrumbItem[] => {
    const breadcrumbs: BreadcrumbItem[] = []
    
    // Home breadcrumb
    breadcrumbs.push({
      text: 'Inicio',
      href: '/',
      disabled: false,
      exact: true,
      icon: 'mdi-home'
    })

    // Current page breadcrumb
    if (route.meta?.breadcrumb && route.path !== '/') {
      breadcrumbs.push({
        text: route.meta.breadcrumb as string,
        href: route.path,
        disabled: true,
        exact: true,
        icon: route.meta.icon as string
      })
    }

    return breadcrumbs
  }

  const toggleSidebar = () => {
    sidebarExpanded.value = !sidebarExpanded.value
    saveState()
  }

  const setSidebarExpanded = (expanded: boolean) => {
    sidebarExpanded.value = expanded
    saveState()
  }

  const setPageTitle = (title: string) => {
    pageTitle.value = title
    document.title = `${title} - MGI System`
  }

  const trackPageView = (path: string, name: string) => {
    const pageView: PageView = {
      path,
      name,
      title: pageTitle.value,
      timestamp: new Date().toISOString()
    }
    
    pageViews.value.unshift(pageView)
    
    // Keep only last 100 page views
    if (pageViews.value.length > 100) {
      pageViews.value = pageViews.value.slice(0, 100)
    }
  }

  const addToHistory = (item: HistoryItem) => {
    // Remove existing item with same path
    navigationHistory.value = navigationHistory.value.filter(h => h.path !== item.path)
    
    // Add to beginning
    navigationHistory.value.unshift(item)
    
    // Keep only last 20 items
    if (navigationHistory.value.length > 20) {
      navigationHistory.value = navigationHistory.value.slice(0, 20)
    }
    
    saveState()
  }

  const addToFavorites = (item: FavoriteItem) => {
    if (!favorites.value.find(f => f.path === item.path)) {
      favorites.value.push(item)
      saveState()
    }
  }

  const removeFromFavorites = (path: string) => {
    favorites.value = favorites.value.filter(f => f.path !== path)
    saveState()
  }

  const toggleFavorite = (route: string, name?: string, title?: string, icon?: string) => {
    const index = favorites.value.findIndex(f => f.path === route)
    if (index !== -1) {
      favorites.value.splice(index, 1)
    } else {
      favorites.value.push({
        path: route,
        name: name || route,
        title: title || name || route,
        icon: icon || 'mdi-star'
      })
    }
    saveState()
  }

  const isFavorite = (route: string): boolean => {
    return favorites.value.some(f => f.path === route)
  }

  const addAnalyticsEvent = (event: {
    category: string
    action: string
    label?: string
    value?: number
    custom_parameters?: Record<string, any>
  }) => {
    analyticsEvents.value.push({
      ...event,
      timestamp: new Date().toISOString()
    })
    
    // Keep only last 1000 events
    if (analyticsEvents.value.length > 1000) {
      analyticsEvents.value = analyticsEvents.value.slice(-1000)
    }
  }

  const setIntendedRoute = (route: string | null) => {
    intendedRoute.value = route
  }

  const getIntendedRoute = (): string | null => {
    return intendedRoute.value
  }

  const clearIntendedRoute = () => {
    intendedRoute.value = null
  }

  const trackNavigation = (category: string, action: string, label?: string) => {
    addAnalyticsEvent({
      category,
      action,
      label,
      custom_parameters: {
        timestamp: Date.now(),
        path: label,
        userAgent: navigator.userAgent
      }
    })
    
    console.log(`🧭 Navigation tracked: ${category} - ${action}${label ? ` (${label})` : ''}`)
  }

  const saveState = () => {
    try {
      const stateToSave = {
        sidebarExpanded: sidebarExpanded.value,
        sidebarOpen: sidebarOpen.value,
        favorites: favorites.value,
        navigationHistory: navigationHistory.value.slice(0, 10) // Save only recent history
      }
      localStorage.setItem('mgi_navigation_state', JSON.stringify(stateToSave))
    } catch (error) {
      console.warn('Failed to save navigation state:', error)
    }
  }

  const loadState = () => {
    try {
      const saved = localStorage.getItem('mgi_navigation_state')
      if (saved) {
        const parsedState = JSON.parse(saved)
        sidebarExpanded.value = parsedState.sidebarExpanded ?? true
        sidebarOpen.value = parsedState.sidebarOpen ?? true
        favorites.value = parsedState.favorites || []
        navigationHistory.value = parsedState.navigationHistory || []
      }
    } catch (error) {
      console.warn('Failed to load navigation state:', error)
    }
  }

  // 🎯 Initialize store
  const initialize = () => {
    loadState()
  }

  return {
    // State
    currentRoute,
    breadcrumbs,
    sidebarExpanded,
    sidebarOpen,
    pageTitle,
    navigationHistory,
    pageViews,
    favorites,
    isLoading,
    analyticsEvents,
    intendedRoute,
    
    // Computed
    canGoBack,
    recentPages,
    favoriteRoutes,
    
    // Actions
    setCurrentRoute,
    setLoading,
    setBreadcrumbs,
    updateBreadcrumbs,
    generateBreadcrumbs,
    toggleSidebar,
    setSidebarExpanded,
    setPageTitle,
    trackPageView,
    addToHistory,
    addToFavorites,
    removeFromFavorites,
    toggleFavorite,
    isFavorite,
    addAnalyticsEvent,
    setIntendedRoute,
    getIntendedRoute,
    clearIntendedRoute,
    trackNavigation,
    saveState,
    loadState,
    initialize
  }
})
